import {
  type LngLat,
  type MapLocationRequest,
  IconButton,
  Image,
  LeftArrowIcon,
  Map as BaseMap,
  MapMarker,
  Route,
} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {tag2url} from '@yandex-delivery/utils'
import {memo, useMemo, useState} from 'react'

import {useConfigContext} from '../../../../contexts/ConfigContext'
import {MAP_ANIMATION_DURATION_IN_MS} from '../../../../lib/constants'
import {getMapLocale} from '../../../../lib/getMapLocale'
import {calculateBounds} from '../../lib/calculateBounds'
import {OrderNumIcon} from '../OrderNumIcon/OrderNumIcon'
import * as styles from './Map.classes'

/** Пропсы компонента Map */
type MapProps = {
  /** Отступ снизу карты. Нужно для резервирования места под дровер с информацией внизу экрана */
  bottomOffset: number
  /** Маршрут */
  route: LngLat[]
  /** Коллбэк на возврат назад */
  onGoBack: VoidFunction
}

/** Тег иконки пина для "Откуда" */
const ADDRESS_SOURCE_PIN_ICON = 'delivery_address_source_pin'

/**
 * Компонент с картой и пином посередине,
 * который отслеживает координаты текущего центра карты при смене позиции пользователем.
 */
export const Map = memo<MapProps>(function Map({bottomOffset, route, onGoBack}) {
  const i18n = useI18n()

  const {lang} = useConfigContext()

  const initialMapLocation = useMemo<MapLocationRequest>(
    () => ({
      bounds: calculateBounds(route),
      duration: MAP_ANIMATION_DURATION_IN_MS,
    }),
    [route],
  )

  const [mapLocation, updateMapLocation] = useState<MapLocationRequest>(initialMapLocation)

  const mapLocale = getMapLocale(lang)

  return (
    <div
      className={styles.map}
      // Ограничиваем высоту карты (styles.borderRadiusHeight - для учёта закруглений блока снизу),
      // резервируя место под информацию о маршруте снизу
      style={{height: `calc(100% - ${bottomOffset ?? 0}px + ${styles.borderRadiusHeight})`}}
    >
      <BaseMap
        errorMessage={i18n('map_load_error')}
        lang={mapLocale}
        location={mapLocation}
        margin={[30, 30, 30, 30]}
        mode="raster"
      >
        <IconButton className={styles.backButton} onPress={onGoBack} shape="circle" variant="floating">
          <LeftArrowIcon />
        </IconButton>
        <Route onBoundsChange={updateMapLocation} points={route} />
        {route.map((coordinates, index) => {
          const isSource = index === 0

          return (
            <MapMarker key={`map-marker-${index}`} coordinates={coordinates}>
              <div className={styles.pinContainer}>
                <div className={styles.pin}>
                  <Image height={72} src={tag2url(ADDRESS_SOURCE_PIN_ICON)} />
                  {!isSource && <OrderNumIcon className={styles.pinOrderNum} color="black" num={index} />}
                </div>
              </div>
            </MapMarker>
          )
        })}
      </BaseMap>
    </div>
  )
})
